import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, FileSearch, ClipboardCheck, GraduationCap, HelpCircle, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useServiceCatalog, useCreateServiceRequest } from './serviceRequestHooks';
import AddressAutocomplete from '../common/AddressAutocomplete';
import US_STATES from '../../constants/usStates';

const CATEGORY_ICONS = {
  compliance: FileSearch,
  cqa: ClipboardCheck,
  training: GraduationCap,
};

const CATEGORY_BLURBS = {
  compliance: 'File reviews, inspections, utility analyses',
  cqa: 'Construction QA and Capital Needs Assessments',
  training: 'Zeffert University programs',
};

const PROGRAM_OPTIONS = [
  { value: 'hud',           label: 'HUD' },
  { value: 'usda',          label: 'USDA' },
  // Leads with the program, not the agency: someone requesting on a manager's
  // behalf knows they run LIHTC without knowing that IRS/Treasury is who governs
  // it. The stored value stays irs_treasury.
  { value: 'irs_treasury',  label: 'LIHTC (IRS / Treasury)' },
  { value: 'state_local',   label: 'State or local' },
  { value: 'unsure',        label: "I'm unsure" },
];

const REFERRAL_OPTIONS = [
  { value: '',                 label: 'Select…' },
  { value: 'referral',         label: 'Referral' },
  { value: 'existing_client',  label: 'Existing Client' },
  { value: 'web_search',       label: 'Web Search' },
  { value: 'social_media',     label: 'Social Media' },
  { value: 'conference',       label: 'Conference / Event' },
  { value: 'email_campaign',   label: 'Email Campaign' },
  { value: 'publication',      label: 'Industry Publication' },
  { value: 'other',            label: 'Other' },
];

const ROLE_OPTIONS = [
  { value: 'developer',                   label: 'Developer' },
  { value: 'owner',                       label: 'Owner' },
  { value: 'individual_owner',            label: 'Individual Owner' },
  { value: 'agent',                       label: 'Agent' },
  { value: 'investor',                    label: 'Investor' },
  { value: 'syndicator',                  label: 'Syndicator' },
  { value: 'property_management_company', label: 'Property Management Company' },
  { value: 'public_housing_authority',    label: 'Public Housing Authority' },
  { value: 'finance_agency',              label: 'Finance Agency' },
  { value: 'other',                       label: 'Other' },
];

export default function RequestServicesWizard({ initialDraft, onSubmitted }) {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = theme === 'dark';
  const { data: catalogData, isLoading: catalogLoading } = useServiceCatalog();
  const create = useCreateServiceRequest();

  const [step, setStep] = useState(1);
  const [unsure, setUnsure] = useState(initialDraft?.unsure || false);
  const [selectedItems, setSelectedItems] = useState(initialDraft?.service_item_slugs || []);
  const [programs, setPrograms] = useState(initialDraft?.program_types || []);

  const [contact, setContact] = useState({
    contact_name:  initialDraft?.contact_name  || currentUser?.full_name || '',
    contact_email: initialDraft?.contact_email || currentUser?.email || '',
    contact_phone: initialDraft?.contact_phone || '',
    company:       initialDraft?.company       || '',
    address:       initialDraft?.address       || '',
    city:          initialDraft?.city          || '',
    state:         initialDraft?.state         || '',
    zip_code:      initialDraft?.zip_code      || '',
    role_classification:      initialDraft?.role_classification || 'developer',
    preferred_contact_method: initialDraft?.preferred_contact_method || 'email',
    notes:           initialDraft?.notes || '',
    referral_source: initialDraft?.referral_source || '',
  });

  useEffect(() => {
    if (unsure) setSelectedItems([]);
  }, [unsure]);

  const catalog = catalogData?.catalog || [];

  const canNextFromStep1 = (unsure || selectedItems.length > 0) && programs.length > 0;
  const canSubmit = !!contact.contact_name && !!contact.contact_email && !!contact.contact_phone;

  const toggleItem = (slug) => {
    if (unsure) setUnsure(false);
    setSelectedItems((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Name, email, and phone are required.');
      return;
    }
    const payload = unsure
      ? { unsure: true, ...contact, program_types: programs }
      : {
          service_item_slugs: selectedItems,
          program_types: programs,
          ...contact,
        };
    if (initialDraft?.source_request_id) payload.source_request_id = initialDraft.source_request_id;

    try {
      const result = await create.mutateAsync(payload);
      toast.success('Service request submitted. We will be in touch.');
      onSubmitted?.(result?.service_request);
    } catch (e) {
      console.error(e);
      toast.error('Could not submit your request. Please try again.');
    }
  };

  const stepBadge = (n, label) => (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
        step === n ? 'bg-blue-500 text-white' :
        step > n   ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                     'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
      }`}>{step > n ? <CheckCircle2 size={14} /> : n}</div>
      <span className={`text-sm font-rasa ${step === n ? 'text-blue-500 font-medium' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
    </div>
  );

  return (
    <div className={`rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} p-6`}>
      <div className="flex items-center gap-6 mb-6 flex-wrap">
        {stepBadge(1, 'Services')}
        <div className={`h-px w-8 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
        {stepBadge(2, 'Contact details')}
      </div>

      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium font-rasa">What services do you need?</h3>
            <p className={`text-sm font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Pick one or more services from any category, or tell us you're unsure and we'll route it for you.
            </p>
          </div>

          {catalogLoading && <div className="text-sm text-gray-400">Loading catalog…</div>}

          <div className="space-y-5">
            {catalog.map((g) => {
              const Icon = CATEGORY_ICONS[g.department.slug] || FileSearch;
              const dimmed = unsure;
              return (
                <div
                  key={g.department.slug}
                  className={`rounded-lg border p-4 transition ${
                    isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-white'
                  } ${dimmed ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <Icon size={20} className={isDark ? 'text-gray-300' : 'text-gray-600'} />
                    <div>
                      <div className="font-medium font-rasa">{g.department.name}</div>
                      <div className={`text-xs font-rasa mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {CATEGORY_BLURBS[g.department.slug]}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {g.service_areas.map((area) => (
                      <div key={area.service_area.slug}>
                        <div className={`text-xs uppercase tracking-wide font-rasa mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {area.service_area.name}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {area.items.map((item) => {
                            const checked = !unsure && selectedItems.includes(item.slug);
                            return (
                              <label
                                key={item.slug}
                                className={`flex items-start gap-2 p-3 rounded-md border transition ${
                                  unsure ? 'cursor-not-allowed' : 'cursor-pointer'
                                } ${
                                  checked
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : isDark
                                      ? 'border-gray-700 hover:border-gray-500'
                                      : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-1 accent-blue-500"
                                  disabled={unsure}
                                  checked={checked}
                                  onChange={() => toggleItem(item.slug)}
                                />
                                <span className="text-sm font-rasa">{item.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => setUnsure((v) => !v)}
              className={`w-full text-left rounded-lg p-4 border transition flex items-start gap-3 ${
                unsure
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : isDark
                    ? 'border-gray-700 hover:border-gray-500 bg-gray-900/40'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <HelpCircle size={20} className={unsure ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-500'} />
              <div>
                <div className="font-medium font-rasa">I'm unsure</div>
                <div className={`text-xs font-rasa mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Tell us a bit about what you need and we'll help figure it out.
                </div>
              </div>
            </button>
          </div>

          <div>
            <div className={`text-xs uppercase tracking-wide font-rasa font-bold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              Applicable programs <span className="text-red-500 normal-case font-normal">*</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {PROGRAM_OPTIONS.map((p) => {
                const on = programs.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() =>
                      setPrograms((prev) =>
                        on ? prev.filter((x) => x !== p.value) : [...prev, p.value]
                      )
                    }
                    className={`px-3 py-1.5 rounded-full text-xs font-rasa font-medium border transition ${
                      on
                        ? 'bg-blue-500 text-white border-blue-500'
                        : isDark
                          ? 'border-gray-500 text-gray-100 hover:border-blue-400 hover:text-white'
                          : 'border-gray-500 text-gray-800 hover:border-blue-500 hover:text-blue-700'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {programs.length === 0 && (
              <p className={`text-[11px] font-rasa mt-1.5 ${isDark ? 'text-orange-300' : 'text-orange-600'}`}>
                Pick at least one program before continuing.
              </p>
            )}
          </div>

          {(unsure || selectedItems.length > 0 || programs.length > 0) && (
            <div className={`rounded-lg border p-4 ${
              isDark
                ? 'border-blue-900/60 bg-gradient-to-br from-blue-900/20 to-gray-900/40'
                : 'border-blue-200 bg-gradient-to-br from-blue-50 to-white'
            }`}>
              <div className="flex items-center gap-1.5 mb-3">
                <CheckCircle2 size={14} className="text-blue-500" />
                <span className={`text-[11px] uppercase tracking-wide font-rasa font-bold ${
                  isDark ? 'text-blue-300' : 'text-blue-700'
                }`}>Your selections</span>
              </div>

              {unsure && (
                <div className={`flex items-start gap-2 mb-3 p-2.5 rounded-md ${
                  isDark ? 'bg-blue-900/30' : 'bg-blue-100/60'
                }`}>
                  <HelpCircle size={14} className="text-blue-500 mt-0.5 shrink-0" />
                  <span className={`text-xs font-rasa ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                    You're unsure. JPRO will route this for you.
                  </span>
                </div>
              )}

              {!unsure && selectedItems.length > 0 && (
                <div className="space-y-3 mb-3">
                  {catalog.map((g) => {
                    const areasWithPicked = g.service_areas
                      .map((a) => ({
                        ...a,
                        picked: a.items.filter((it) => selectedItems.includes(it.slug)),
                      }))
                      .filter((a) => a.picked.length > 0);
                    if (areasWithPicked.length === 0) return null;
                    const deptTotal = areasWithPicked.reduce((n, a) => n + a.picked.length, 0);
                    const Icon = CATEGORY_ICONS[g.department.slug] || FileSearch;
                    return (
                      <div key={g.department.slug}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Icon size={13} className="text-blue-500" />
                          <span className={`text-[11px] font-rasa font-bold uppercase tracking-wide ${
                            isDark ? 'text-gray-200' : 'text-gray-700'
                          }`}>{g.department.name}</span>
                          <span className={`text-[10px] font-rasa ${
                            isDark ? 'text-gray-500' : 'text-gray-400'
                          }`}>· {deptTotal}</span>
                        </div>
                        <div className="space-y-2 pl-5">
                          {areasWithPicked.map((a) => (
                            <div key={a.service_area.slug}>
                              <div className={`text-[10px] font-rasa font-semibold mb-1 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}>{a.service_area.name}</div>
                              <div className="flex flex-wrap gap-1.5">
                                {a.picked.map((it) => (
                                  <span key={it.slug} className={`text-[11px] font-rasa font-medium px-2 py-0.5 rounded-full ${
                                    isDark ? 'bg-blue-900/50 text-blue-200 border border-blue-800' : 'bg-blue-100 text-blue-700 border border-blue-200'
                                  }`}>
                                    {it.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {programs.length > 0 && (
                <div className={`pt-3 ${(unsure || selectedItems.length > 0) ? (isDark ? 'border-t border-blue-900/40' : 'border-t border-blue-200/60') : ''}`}>
                  <div className={`text-[11px] font-rasa font-bold uppercase tracking-wide mb-1.5 ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  }`}>Programs <span className={`text-[10px] font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {programs.length}</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {PROGRAM_OPTIONS.filter((p) => programs.includes(p.value)).map((p) => (
                      <span key={p.value} className={`text-[11px] font-rasa font-medium px-2 py-0.5 rounded-full ${
                        isDark ? 'bg-blue-900/50 text-blue-200 border border-blue-800' : 'bg-blue-100 text-blue-700 border border-blue-200'
                      }`}>
                        {p.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="button"
              disabled={!canNextFromStep1}
              onClick={() => setStep(2)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium font-rasa transition ${
                canNextFromStep1
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium font-rasa">Tell us how to reach you</h3>
          <p className={`text-sm font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>We've prefilled what we know. Adjust anything that's off.</p>

          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Full name *" value={contact.contact_name} onChange={(v) => setContact({ ...contact, contact_name: v })} isDark={isDark} />
            <TextField label="Email *" type="email" value={contact.contact_email} onChange={(v) => setContact({ ...contact, contact_email: v })} isDark={isDark} />
            <TextField label="Phone *" value={contact.contact_phone} onChange={(v) => setContact({ ...contact, contact_phone: v })} isDark={isDark} />
            <TextField label="Company" value={contact.company} onChange={(v) => setContact({ ...contact, company: v })} isDark={isDark} />
            <SelectField label="Your role" value={contact.role_classification} onChange={(v) => setContact({ ...contact, role_classification: v })} options={ROLE_OPTIONS} isDark={isDark} />
            <SelectField
              label="Preferred contact method"
              value={contact.preferred_contact_method}
              onChange={(v) => setContact({ ...contact, preferred_contact_method: v })}
              options={[{ value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' }]}
              isDark={isDark}
            />
            <SelectField
              label="How did you hear about us?"
              value={contact.referral_source}
              onChange={(v) => setContact({ ...contact, referral_source: v })}
              options={REFERRAL_OPTIONS}
              isDark={isDark}
            />
          </div>

          <div>
            <label className={`block text-xs uppercase tracking-wide font-rasa mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Street address <span className="normal-case text-[10px] text-gray-500 font-normal">picking one fills City, State, ZIP</span>
            </label>
            <AddressAutocomplete
              value={contact.address}
              onChange={(val) => setContact((prev) => ({ ...prev, address: val }))}
              onPlaceSelect={(p) => setContact((prev) => ({
                ...prev,
                address:  p.address  || prev.address,
                city:     p.city     || prev.city,
                state:    p.state    || prev.state,
                zip_code: p.zip_code || prev.zip_code,
              }))}
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <TextField label="City" value={contact.city} onChange={(v) => setContact({ ...contact, city: v })} isDark={isDark} />
            <SelectField
              label="State"
              value={contact.state}
              onChange={(v) => setContact({ ...contact, state: v })}
              options={[{ value: '', label: 'Select' }, ...US_STATES.map((s) => ({ value: s.value, label: s.value }))]}
              isDark={isDark}
            />
            <TextField label="ZIP code" value={contact.zip_code} onChange={(v) => setContact({ ...contact, zip_code: v })} isDark={isDark} />
          </div>

          <div>
            <label className={`block text-xs uppercase tracking-wide font-rasa mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tell us about your project</label>
            <textarea
              className={`w-full rounded-md border px-3 py-2 text-sm font-rasa ${
                isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              rows={4}
              value={contact.notes}
              onChange={(e) => setContact({ ...contact, notes: e.target.value })}
              placeholder={unsure ? 'Describe what you need help with...' : 'Anything else we should know?'}
            />
          </div>

          <div className="flex justify-between pt-4">
            <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-rasa text-gray-500 hover:text-gray-700">
              <ArrowLeft size={16} /> Back
            </button>
            <button
              type="button"
              disabled={!canSubmit || create.isPending}
              onClick={handleSubmit}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-md font-medium font-rasa ${
                canSubmit && !create.isPending
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Send size={16} />
              {create.isPending ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', isDark }) {
  return (
    <div>
      <label className={`block text-xs uppercase tracking-wide font-rasa mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border px-3 py-2 text-sm font-rasa ${
          isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, isDark }) {
  return (
    <div>
      <label className={`block text-xs uppercase tracking-wide font-rasa mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border px-3 py-2 text-sm font-rasa ${
          isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
